import React from 'react';
import { Modal } from 'components/modal';
import { DocsIcon, XIcon } from 'assets';
import { copyToClipBoard } from 'helpers';
import { useToast } from 'hooks';

interface DocumentPreviewModalProps {
  show: boolean;
  onClose: () => void;
  /** Filename / heading shown at the top of the sheet. */
  name: string;
  /** "Job Description", "CV", … */
  type: string;
  /** The full text the chip stands in for. */
  content: string;
}

/**
 * Read-only preview for a document chip in the chat transcript.
 *
 * Chips collapse a long pasted block down to a filename, which means the actual
 * content is otherwise unreachable once it's been sent. This shows it back.
 */
const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({
  show,
  onClose,
  name,
  type,
  content,
}) => {
  const { openToast } = useToast();

  const handleCopy = () => {
    copyToClipBoard({
      link: content,
      onSuccess: () =>
        openToast({
          show: true,
          heading: 'Copied',
          text: 'Document content copied to clipboard.',
          type: 'info',
          timeOut: 3000,
        }),
      onError: () =>
        openToast({
          show: true,
          heading: 'Sorry',
          text: 'Could not copy to clipboard.',
          type: 'error',
          timeOut: 3000,
        }),
    });
  };

  return (
    // The shared Modal caps content at 450px and adds its own padding; this sheet
    // is a document reader, so it takes over both.
    <Modal
      show={show}
      close={onClose}
      position="centered"
      contentClassName="!max-w-[760px] !p-0"
    >
      <div className="flex max-h-[80vh] w-full flex-col rounded-[10px] bg-white">
        <header className="flex items-start justify-between gap-4 border-b border-[#EAECF0] px-6 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#EFF4FF]">
              <DocsIcon style={{ color: '#012169' }} />
            </div>
            <div className="min-w-0">
              <p className="truncate text-base font-semibold text-[#1D2939]">{name}</p>
              <p className="text-sm text-[#667085]">{type}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close preview"
            className="shrink-0 rounded p-1 text-[#667085] transition-colors hover:bg-[#F2F4F7]"
          >
            <XIcon />
          </button>
        </header>

        <div className="overflow-y-auto px-6 py-5">
          <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-relaxed text-[#344054]">
            {content}
          </pre>
        </div>

        <footer className="flex justify-end gap-3 border-t border-[#EAECF0] px-6 py-4">
          <button
            type="button"
            onClick={handleCopy}
            className="rounded-lg border border-[#D0D5DD] px-4 py-2 text-sm font-medium text-[#344054] transition-colors hover:bg-[#F9FAFB]"
          >
            Copy text
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-[#012169] px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
          >
            Close
          </button>
        </footer>
      </div>
    </Modal>
  );
};

export { DocumentPreviewModal };
